import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';
import { useLoginMutation } from '../../application/authMutations';
import { useAuth } from '../context/AuthContext';
import { ForgotPasswordModal } from '../components/ForgotPasswordModal';
import { useDeviceEnrollState } from '@/modules/devices/presentation/context/useDeviceEnrollState';

const loginSchema = z.object({
  email: z.string().email('Correo inválido').min(1),
  password: z.string().min(1, 'La contraseña es requerida'),
});
type LoginInput = z.infer<typeof loginSchema>;

export function LoginPage() {
  const { C, T } = useTheme();
  const navigate = useNavigate();
  const auth = useAuth();
  const loginMutation = useLoginMutation();
  const { t, i18n } = useTranslation('common');
  const { trigger: triggerEnroll } = useDeviceEnrollState();

  const [focusField, setFocusField] = useState<'email' | 'pwd' | ''>('');
  const [showPwd, setShowPwd] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [langOpen, setLangOpen] = useState(false);

  // Fallback: any unrecognized code (e.g. 'fr') defaults to 'es'
  const activeLang = (i18n.language ?? 'es').startsWith('en') ? 'en' : 'es';

  const { register, handleSubmit, watch } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const { ref: emailRef, onBlur: emailOnBlur, ...emailRest } = register('email');
  const { ref: pwdRef, onBlur: pwdOnBlur, ...pwdRest } = register('password');

  const emailValue = watch('email');

  const onSubmit = (data: LoginInput) => {
    setLoginError('');
    loginMutation.mutate(data, {
      onSuccess: (session) => {
        auth.saveSession(session);
        // Wait for enroll to complete before navigating.
        // The backend has an HTTP/2 END_STREAM bug that causes fetch() to stall
        // during navigation — running the enroll first avoids the race.
        // Pass idInstance and backend timezone to compare with browser timezone.
        void triggerEnroll(session.idInstance, session.timezone).then(() => navigate('/select-profile'));
      },
      onError: (err) => {
        if (err.kind === 'authentication') {
          setLoginError(t('auth.errorCredentials'));
        } else if (err.kind === 'network' || err.kind === 'timeout') {
          setLoginError(t('auth.errorNetwork'));
        } else {
          setLoginError(t('auth.errorGeneric'));
        }
      },
    });
  };

  const isLoading = loginMutation.isPending;

  return (
    <div
      style={{
        width: '100%',
        minHeight: '100vh',
        background: T.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflowX: 'hidden',
        overflowY: 'auto',
        fontFamily: 'inherit',
        padding: '32px 24px',
        paddingBottom: '10vh',
        position: 'relative',
        boxSizing: 'border-box',
      }}
    >
      {/* ambient gradients — clipped to the viewport box so the decorative
          overflow never produces horizontal/vertical scrollbars */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
        <div style={{ position: 'absolute', top: -200, right: -200, width: 520, height: 520, borderRadius: '50%', background: `radial-gradient(closest-side, ${C.blue}14, transparent 70%)` }} />
        <div style={{ position: 'absolute', bottom: -220, left: -160, width: 560, height: 560, borderRadius: '50%', background: `radial-gradient(closest-side, ${C.blue}0C, transparent 70%)` }} />
      </div>

      {/* card */}
      <div
        data-testid="login-card"
        style={{
          width: '100%',
          maxWidth: 452,
          background: T.surfaceBg,
          borderRadius: 22,
          padding: '40px 42px 46px',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          zIndex: 1,
          border: `1px solid ${T.border}`,
          boxShadow: '0 24px 60px rgba(16,24,40,.16), 0 6px 18px rgba(16,24,40,.08)',
        }}
      >
        {/* logo + language selector */}
        <div style={{ position: 'relative', alignSelf: 'center', marginTop: 10, marginBottom: 14 }}>
          <img
            src="/logo-myintelli-clock-full.png"
            alt="MyIntelli Clock"
            style={{ height: 68, width: 'auto', display: 'block' }}
          />
          {/* language selector anchored to top-right of logo */}
          <div style={{ position: 'absolute', top: -34, right: -104, zIndex: 5 }}>
            <button
              type="button"
              data-testid="lang-toggle"
              onClick={() => { setLangOpen((o) => !o); }}
              title="Language"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                padding: '8px 9px',
                borderRadius: 9,
                border: `1px solid ${langOpen ? C.blue : T.border}`,
                background: T.surfaceBg,
                color: T.text,
                fontFamily: 'inherit',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 1px 3px rgba(0,0,0,.06)',
              }}
            >
              <span>{activeLang === 'es' ? '🇪🇸 ES' : '🇺🇸 EN'}</span>
              <svg
                width={11} height={11} viewBox="0 0 24 24" fill="none"
                stroke={T.textMuted} strokeWidth="2.5" strokeLinecap="round"
                style={{ transform: langOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {langOpen && (
              <>
                <div
                  onClick={() => { setLangOpen(false); }}
                  style={{ position: 'fixed', inset: 0, zIndex: 4 }}
                />
                <div style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  right: 0,
                  zIndex: 6,
                  background: T.surfaceBg,
                  border: `1px solid ${T.border}`,
                  borderRadius: 10,
                  boxShadow: T.shadow,
                  overflow: 'hidden',
                  minWidth: 92,
                }}>
                  {([{ k: 'es', l: '🇪🇸 ES' }, { k: 'en', l: '🇺🇸 EN' }] as const).map((o) => (
                    <button
                      key={o.k}
                      type="button"
                      data-testid={`lang-option-${o.k}`}
                      onClick={() => { void i18n.changeLanguage(o.k); setLangOpen(false); }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        padding: '9px 12px',
                        border: 'none',
                        background: activeLang === o.k ? `${C.blue}10` : 'transparent',
                        color: T.text,
                        fontFamily: 'inherit',
                        fontSize: 12,
                        fontWeight: activeLang === o.k ? 700 : 500,
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.blue, letterSpacing: '.2em', marginBottom: 10, textAlign: 'center' }}>
            {t('auth.welcome')}
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: T.clockText, margin: '0 0 36px', letterSpacing: '-.02em', textAlign: 'center' }}>
            {t('auth.signIn')}
          </h1>

          {/* email */}
          <label style={{ fontSize: 11, fontWeight: 700, color: T.text, marginBottom: 10 }}>
            {t('auth.emailLabel')}
          </label>
          <div style={{ position: 'relative', marginBottom: 26 }}>
            <svg
              width={15} height={15} viewBox="0 0 24 24" fill="none"
              stroke={focusField === 'email' ? C.blue : T.textMuted}
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', transition: 'stroke .15s' }}
            >
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
            </svg>
            <input
              type="email"
              {...emailRest}
              ref={(el) => { emailRef(el); }}
              onFocus={() => { setFocusField('email'); }}
              onBlur={(e) => { setFocusField(''); void emailOnBlur(e); }}
              placeholder={t('auth.emailPlaceholder')}
              style={{
                width: '100%',
                padding: '15px 14px 15px 40px',
                borderRadius: 11,
                border: `1.5px solid ${focusField === 'email' ? C.blue : T.border}`,
                background: T.bg,
                color: T.text,
                fontFamily: 'inherit',
                fontSize: 13,
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color .15s, box-shadow .15s',
                boxShadow: focusField === 'email' ? `0 0 0 4px ${C.blue}18` : 'none',
              }}
            />
          </div>

          {/* password row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: T.text }}>{t('auth.passwordLabel')}</label>
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); setForgotOpen(true); }}
              onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; e.currentTarget.style.textUnderlineOffset = '3px'; }}
              onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
              style={{ fontSize: 11, fontWeight: 600, color: C.blue, textDecoration: 'none', transition: 'color .15s' }}
            >
              {t('auth.forgotPassword')}
            </a>
          </div>
          <div style={{ position: 'relative', marginBottom: 32 }}>
            <svg
              width={15} height={15} viewBox="0 0 24 24" fill="none"
              stroke={focusField === 'pwd' ? C.blue : T.textMuted}
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', transition: 'stroke .15s' }}
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <input
              type={showPwd ? 'text' : 'password'}
              {...pwdRest}
              ref={(el) => { pwdRef(el); }}
              onFocus={() => { setFocusField('pwd'); }}
              onBlur={(e) => { setFocusField(''); void pwdOnBlur(e); }}
              placeholder={t('auth.passwordPlaceholder')}
              style={{
                width: '100%',
                padding: '15px 42px 15px 40px',
                borderRadius: 11,
                border: `1.5px solid ${focusField === 'pwd' ? C.blue : T.border}`,
                background: T.bg,
                color: T.text,
                fontFamily: 'inherit',
                fontSize: 13,
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color .15s, box-shadow .15s',
                boxShadow: focusField === 'pwd' ? `0 0 0 4px ${C.blue}18` : 'none',
              }}
            />
            <button
              type="button"
              onClick={() => { setShowPwd((v) => !v); }}
              aria-label={t('auth.showPassword')}
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', width: 28, height: 28, border: 'none', background: 'transparent', cursor: 'pointer', color: T.textMuted, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6 }}
            >
              {showPwd ? (
                <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>

          {/* error message */}
          {loginError && (
            <div style={{ fontSize: 12, color: C.danger, marginBottom: 14, textAlign: 'center', fontWeight: 600 }}>
              {loginError}
            </div>
          )}

          {/* submit */}
          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: 10,
              border: 'none',
              background: isLoading ? `${C.blue}99` : C.blue,
              color: '#fff',
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: 700,
              cursor: isLoading ? 'wait' : 'pointer',
              boxShadow: `0 8px 20px ${C.blue}40`,
              transition: 'transform .1s, background .15s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
            onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(.98)'; }}
            onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            {isLoading && (
              <svg width={14} height={14} viewBox="0 0 24 24" className="mi-anim-spin" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
            )}
            {isLoading ? t('auth.signingIn') : t('auth.submit')}
          </button>

          {/* SSO divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '30px 0 22px' }}>
            <div style={{ flex: 1, height: 1, background: T.border }} />
            <div style={{ fontSize: 10, fontWeight: 600, color: T.textMuted, letterSpacing: '.08em' }}>{t('auth.orContinueWith')}</div>
            <div style={{ flex: 1, height: 1, background: T.border }} />
          </div>

          {/* SSO buttons */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <button
              type="button"
              disabled
              aria-disabled="true"
              title={t('auth.comingSoon')}
              style={{ padding: '11px', borderRadius: 10, border: `1.5px solid ${T.border}`, background: T.surfaceBg, color: T.textMuted, fontFamily: 'inherit', fontSize: 12, fontWeight: 600, cursor: 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: .55 }}
            >
              <svg width={16} height={16} viewBox="0 0 48 48" style={{ filter: 'grayscale(1)' }}>
                <path fill="#4285F4" d="M46 24.5c0-1.7-.2-3-.4-4.4H24v8.4h12.4c-.5 2.9-2.2 5.3-4.7 7v5.8h7.6c4.4-4.1 6.7-10.1 6.7-16.8z" />
                <path fill="#34A853" d="M24 47c6.4 0 11.7-2.1 15.6-5.7l-7.6-5.8c-2.1 1.4-4.8 2.2-8 2.2-6.1 0-11.3-4.1-13.2-9.6H3v6C6.9 41.5 14.8 47 24 47z" />
                <path fill="#FBBC05" d="M10.8 28.1c-.5-1.4-.8-3-.8-4.6s.3-3.2.8-4.6V12.9H3C1.1 16.6 0 20.2 0 23.5s1.1 6.9 3 10.6l7.8-6z" />
                <path fill="#EA4335" d="M24 9.8c3.5 0 6.6 1.2 9.1 3.5l6.7-6.7C35.7 3.1 30.4 1 24 1 14.8 1 6.9 6.5 3 14.9l7.8 6c1.9-5.5 7.1-9.6 13.2-9.6z" />
              </svg>
              Google
            </button>
            <button
              type="button"
              disabled
              aria-disabled="true"
              title={t('auth.comingSoon')}
              style={{ padding: '11px', borderRadius: 10, border: `1.5px solid ${T.border}`, background: T.surfaceBg, color: T.textMuted, fontFamily: 'inherit', fontSize: 12, fontWeight: 600, cursor: 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: .55 }}
            >
              <svg width={15} height={15} viewBox="0 0 23 23" style={{ filter: 'grayscale(1)' }}>
                <rect x="1" y="1" width="10" height="10" fill="#F25022" />
                <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
                <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
                <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
              </svg>
              Microsoft
            </button>
          </div>

          <div style={{ fontSize: 10, color: T.textMuted, textAlign: 'center', marginTop: 36, letterSpacing: '.06em', opacity: .7 }}>
            {t('auth.footer')}
          </div>
        </form>
      </div>

      {forgotOpen && (
        <ForgotPasswordModal
          initialEmail={emailValue}
          onClose={() => { setForgotOpen(false); }}
        />
      )}
    </div>
  );
}
